import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { axiosInstance } from "../../api/api";
import { hooks } from "../../hooks";
import { Routes } from "../../routes";

interface Patrol {
  id: number;
  name: string;
}

interface User {
  id: number;
  name: string;
  email?: string;
}

interface Message {
  type: "success" | "error" | "info";
  text: string;
}

interface FormData {
  patrol_id: string;
  user_id: string;
}

const usePatrolUserForm = (id?: string) => {
  const isEdit = Boolean(id);
  const [patrols, setPatrols] = useState<Patrol[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [formData, setFormData] = useState<FormData>({
    patrol_id: "",
    user_id: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  

  const showMessage = (type: Message["type"], text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const isSecurityUser = (employee: any): boolean => {
    const roleName = (employee.role?.name || employee.role?.label || '').toLowerCase();
    return roleName.includes('security');
  };

  const handleApiError = (err: any) => {
    if (err.response?.status === 429) {
      showMessage("error", "Terlalu banyak request ke server. Silakan tunggu 1-2 menit sebelum mencoba lagi.");
    } else if (err.code === 'ECONNABORTED') {
      showMessage("error", "Koneksi timeout. Coba lagi.");
    } else {
      showMessage("error", "Gagal memuat data. Silakan coba kembali.");
    }
  };

  const fetchOptions = async () => {
    try {
      setLoading(true);
      showMessage("info", "Memuat data user...");

      const patrolRes = await axiosInstance.get("/api/v1/patrol/index");
      setPatrols(patrolRes.data?.data || patrolRes.data || []);

      const firstPageRes = await axiosInstance.get("/api/ap/v1/employee", {
        params: { page: 1, per_page: 50, role: 'security' }
      });

      const meta = firstPageRes.data?.meta;
      const totalPages = meta?.last_page || 1;

      let allUsers = firstPageRes.data?.data || firstPageRes.data || [];
      let securityUsers = allUsers.filter(isSecurityUser);

      if (totalPages > 1) {
        const batches = [];
        for (let page = 2; page <= totalPages; page += 5) {
          batches.push(Array.from({ length: Math.min(5, totalPages - page + 1) }, (_, i) => page + i));
        }

        for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
          const batchPages = batches[batchIndex];
          const batchPromises = batchPages.map(page => 
            axiosInstance.get("/api/ap/v1/employee", { params: { page, per_page: 50, role: 'security' } })
          );
          const batchResponses = await Promise.all(batchPromises);

          batchResponses.forEach((response) => {
            const pageData = response.data?.data || response.data || [];
            allUsers = [...allUsers, ...pageData];
            const pageSecurity = pageData.filter(isSecurityUser);
            securityUsers = [...securityUsers, ...pageSecurity];
          });

          if (batchIndex < batches.length - 1) await new Promise(r => setTimeout(r, 1000));
        }
      }

      const mappedUsers = securityUsers.map((employee: any) => ({
        id: employee.user_id,
        name: `${employee.employee_personal?.full_name || 'Unknown'}(${employee.user_id})`,
        email: employee.user?.email || ''
      }));

      setUsers(mappedUsers);

      if (securityUsers.length === 0) showMessage("error", "Tidak ditemukan security users");

    } catch (err: any) {
      console.error("Error:", err);
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingData = async () => {
    if (!isEdit || !id) return;

    try {
      setLoading(true);
      const res = await axiosInstance.get(`/api/v1/patrol/user/show/${id}`);
      const data = res.data?.data || res.data;
      setFormData({
        patrol_id: String(data.patrol_id || ""),
        user_id: String(data.user_id || "")
      });
    } catch (err: any) {
      console.error("Error fetching patrol user data:", err);
      showMessage("error", "Gagal memuat data patrol user");
    } finally {
      setLoading(false);
    }
  };

  const submitForm = async (): Promise<boolean> => {
    try {
      setSubmitting(true);
      const payload = {
        patrol_id: formData.patrol_id,
        user_id: formData.user_id,
      };
      if (isEdit) {
        await axiosInstance.put(`/api/v1/patrol/user/update/${id}`, payload);
        showMessage("success", "Data patrol user berhasil diperbarui");
      } else {
        await axiosInstance.post("/api/v1/patrol/user/store", payload);
        showMessage("success", "Data patrol user berhasil ditambahkan");
      }
      return true;
    } catch (err: any) {
      console.error("Error saving patrol user:", err);
      showMessage("error", err.response?.data?.message || (isEdit ? "Gagal memperbarui data" : "Gagal menambahkan data"));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const updateFormData = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return {
    isEdit,
    patrols,
    users,
    formData,
    loading,
    submitting,
    message,

    fetchOptions,
    fetchExistingData,
    submitForm,
    updateFormData,
    showMessage
  };
};

const MessageAlert: React.FC<{ message: Message | null }> = ({ message }) => {
  if (!message) return null;

  const alertStyles = {
    success: {
      backgroundColor: "#d1fae5",
      color: "#065f46",
      border: "1px solid #a7f3d0",
      icon: "✅"
    },
    error: {
      backgroundColor: "#fee2e2",
      color: "#991b1b",
      border: "1px solid #fecaca",
      icon: "❌"
    },
    info: {
      backgroundColor: "#dbeafe",
      color: "#1e40af",
      border: "1px solid #bfdbfe",
      icon: "ℹ️"
    },
  };

  return (
    <div
      style={{
        ...alertStyles[message.type],
        padding: "12px 16px",
        borderRadius: "8px",
        marginBottom: "20px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "14px",
        fontWeight: "500"
      }}
    >
      <span>{alertStyles[message.type].icon}</span>
      <span>{message.text}</span>
    </div>
  );
};

const LoadingSpinner: React.FC<{ text?: string }> = ({ text = "Memuat..." }) => (
  <div style={styles.loadingContainer}>
    <div style={styles.spinner}></div>
    <p style={{
      fontSize: "16px",
      textAlign: "center",
      margin: 0,
      color: "#6b7280"
    }}>{text}</p>
  </div>
);

const FormSelect: React.FC<{
  label: string;
  value: string;
  options: Array<{ id: number; name: string; email?: string }>;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
}> = ({ label, value, options, onChange, disabled = false, required = true }) => {
  
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value);
  };

  return (
    <div style={styles.formGroup}>
      <label style={styles.label}>
        {label}
        {required && <span style={styles.required}>*</span>}
      </label>
      
      <select
        value={value}
        onChange={handleChange}
        disabled={disabled}
        style={{
          ...styles.select,
          ...(disabled ? styles.disabled : {}),
          padding: "12px 16px"
        }}
        required={required}
      >
        <option value="">-- Pilih {label} --</option>
        {options.map((option) => (
          <option key={option.id} value={String(option.id)}>
            {option.name} {option.email && `(${option.email})`}
          </option>
        ))}
      </select>
    </div>
  );
};

const ActionButtons: React.FC<{
  onCancel: () => void;
  onSubmit: () => void;
  submitting: boolean;
  isEdit: boolean;
}> = ({ onCancel, onSubmit, submitting, isEdit }) => (
  <div style={styles.buttonGroup}>
    <button
      type="button"
      onClick={onCancel}
      disabled={submitting}
      style={styles.cancelButton}
    >
      Batal
    </button>
    <button
      type="button"
      onClick={onSubmit}
      disabled={submitting}
      style={styles.submitButton}
    >
      {submitting ? (
        <span style={styles.buttonContent}>
          <div style={styles.smallSpinner}></div>
          Menyimpan...
        </span>
      ) : (
        isEdit ? "Perbarui" : "Simpan"
      )}
    </button>
  </div>
);

export const PatrolUserForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    isEdit,
    patrols,
    users,
    formData,
    loading,
    submitting,
    message,

    fetchOptions,
    fetchExistingData,
    submitForm,
    updateFormData,
    showMessage
  } = usePatrolUserForm(id);

  useEffect(() => {
    const initializeData = async () => {
      await fetchOptions();
      if (isEdit) {
        await fetchExistingData();
      }
    };

    initializeData();
  }, [id, isEdit]);

  const showCustomAlert = (type: "success" | "error" | "warning", title: string, message: string) => {
    const alertEl = document.createElement('div');
    alertEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 16px 20px;
      border-radius: 8px;
      color: white;
      font-weight: 500;
      z-index: 10000;
      background-color: ${type === 'warning' ? '#f59e0b' : type === 'error' ? '#ef4444' : '#10b981'};
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      transform: translateX(100%);
      transition: transform 0.3s ease;
      max-width: 350px;
    `;
    alertEl.innerHTML = `
      <div style="font-weight: 600; margin-bottom: 4px;">${title}</div>
      <div style="font-size: 14px;">${message}</div>
    `;
    document.body.appendChild(alertEl);

    setTimeout(() => { alertEl.style.transform = 'translateX(0)' }, 100);
    setTimeout(() => { 
      alertEl.style.transform = 'translateX(100%)';
      setTimeout(() => { 
        if (document.body.contains(alertEl)) {
          document.body.removeChild(alertEl);
        }
      }, 300);
    }, 4000);
  };

  const showCustomConfirm = (title: string, message: string, type: "warning" | "danger" = "warning"): Promise<boolean> => {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        padding: 16px;
      `;

      const modal = document.createElement('div');
      modal.style.cssText = `
        background: white;
        padding: 24px;
        border-radius: 12px;
        max-width: 400px;
        width: 100%;
        box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
        text-align: center;
      `;

      modal.innerHTML = `
        <div style="font-size: 18px; font-weight: 600; color: #1f2937; margin-bottom: 8px;">
          ${title}
        </div>
        <div style="color: #6b7280; margin-bottom: 24px; line-height: 1.5;">
          ${message}
        </div>
        <div style="display: flex; gap: 12px; justify-content: center;">
          <button id="confirm-cancel" style="
            padding: 10px 20px;
            border: 1px solid #6b7280;
            background: white;
            color: #6b7280;
            border-radius: 8px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;

          ">Batal</button>
          <button id="confirm-ok" style="
            padding: 10px 20px;
            border: none;
            background: ${type === 'danger' ? '#dc2626' : '#f59e0b'};
            color: white;
            border-radius: 8px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;

          ">Ya, Lanjutkan</button>
        </div>
      `;

      overlay.appendChild(modal);
      document.body.appendChild(overlay);

      const handleConfirm = () => {
        document.body.removeChild(overlay);
        resolve(true);
      };

      const handleCancel = () => {
        document.body.removeChild(overlay);
        resolve(false);
      };

      modal.querySelector('#confirm-ok')?.addEventListener('click', handleConfirm);
      modal.querySelector('#confirm-cancel')?.addEventListener('click', handleCancel);
    });
  };

  const handleSubmit = async () => {
    if (!formData.patrol_id || !formData.user_id) {
      showCustomAlert("warning", "Form tidak lengkap", "Harap pilih patrol dan user");
      return;
    }

    const success = await submitForm();
    if (success) {
      setTimeout(() => {
        navigate("/patrol-user");
      }, 1500);
    }
  };

  const handleCancel = () => {
    if (formData.patrol_id !== "" || formData.user_id !== "") {
      showCustomConfirm(
        "Batalkan Perubahan",
        "Anda memiliki perubahan yang belum disimpan. Yakin ingin keluar?",
        "warning"
      ).then((confirmed) => {
        if (confirmed) {
          navigate("/patrol-user");
        }
      });
    } else {
      navigate("/patrol-user");
    }
  };

  if (loading) {
    return <LoadingSpinner 
      text={isEdit ? "Memuat data..." : "Mempersiapkan form..."} 

    />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.backContainer}>
        <button 
          onClick={handleCancel}
          style={styles.backButton}
        >
          <span>←</span>
        </button>
      </div>

      <div style={styles.header}>
        <h1 style={styles.title}>
          {isEdit ? "Edit Assignment Patrol" : "Tambah Assignment Patrol"}
        </h1>
        <p style={styles.subtitle}>
          {isEdit 
            ? "Perbarui assignment patrol dan user" 
            : "Tambahkan assignment patrol baru untuk user"
          }
        </p>
      </div>

      <MessageAlert message={message} />

      <div style={styles.formContainer}>
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} style={styles.form}>
          <FormSelect
            label="Patrol"
            value={formData.patrol_id}
            options={patrols}
            onChange={(value) => updateFormData("patrol_id", value)}
            disabled={submitting}

          />

          <FormSelect
            label="User"
            value={formData.user_id}
            options={users}
            onChange={(value) => updateFormData("user_id", value)}
            disabled={submitting}

          />

          <ActionButtons
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            submitting={submitting}
            isEdit={isEdit}

          />
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: "600px",
    margin: "0 auto",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    backgroundColor: "#f8fafc",
    padding: "20px"
  },
  backContainer: {
    marginBottom: "20px"
  },
  backButton: {
    backgroundColor: "#6b7280",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    padding: "10px 16px",
    fontSize: "14px"
  },
  header: {
    marginBottom: "30px",
    textAlign: "center"
  },
  title: {
    fontWeight: "700",
    color: "#1f2937",
    margin: "0 0 8px 0",
    fontSize: "24px"
  },
  subtitle: {
    color: "#6b7280",
    margin: 0,
    lineHeight: "1.5",
    fontSize: "14px"
  },
  formContainer: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
    padding: "24px"
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px"
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  label: {
    fontWeight: "500",
    color: "#374151",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "14px"
  },
  required: {
    color: "#dc2626"
  },
  select: {
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    backgroundColor: "#fff",
    transition: "border-color 0.2s, box-shadow 0.2s",
    width: "100%",
    boxSizing: "border-box" as const,
    fontSize: "14px"
  },
  disabled: {
    backgroundColor: "#f9fafb",
    color: "#6b7280",
    cursor: "not-allowed"
  },
  buttonGroup: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "8px",
    gap: "12px"
  },
  cancelButton: {
    border: "1px solid #6b7280",
    borderRadius: "8px",
    backgroundColor: "transparent",
    color: "#6b7280",
    cursor: "pointer",
    fontWeight: "500",
    transition: "all 0.2s",
    padding: "12px 24px",
    fontSize: "14px"
  },
  submitButton: {
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#059669",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "500",
    transition: "background-color 0.2s",
    minWidth: "120px",
    padding: "12px 24px",
    fontSize: "14px"
  },
  buttonContent: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    justifyContent: "center"
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    padding: "60px 20px"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #059669",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  smallSpinner: {
    width: "16px",
    height: "16px",
    border: "2px solid transparent",
    borderTop: "2px solid #fff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  }
};

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerHTML = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleSheet);
}